import { useRef } from "react";
import { Paperclip, X } from "lucide-react";

const OUT_CATEGORIES = [
  "💳 Business expense",
  "🏠 Personal / owner draw",
  "🔁 Transfer between accounts",
  "🤝 Loan (given or received)",
  "↩️ Refund",
  "❓ Other (see note)",
];
const IN_CATEGORIES = [
  "💰 Income / sale",
  "🤝 Loan (given or received)",
  "🏠 Personal / owner draw",
  "🔁 Transfer between accounts",
  "↩️ Refund",
  "❓ Other (see note)",
];
const TAX_OPTIONS = ["GST only (5%)", "GST + PST (12%)", "No tax / exempt", "Not sure"];

const fmtMoney = (amt) => {
  const n = Math.abs(Number(amt) || 0);
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d + "T00:00:00");
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const Chip = ({ label, selected, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="gb-transition text-[13px] font-medium rounded-full px-3.5 py-2 border active:scale-95"
    style={
      selected
        ? { backgroundColor: "#16a34a", color: "#fff", borderColor: "#16a34a" }
        : { backgroundColor: "#f4f8f5", color: "#2b4a3c", borderColor: "#dbe7e0" }
    }
  >
    {label}
  </button>
);

export default function TransactionCard({ item, index, answer, onChange }) {
  const fileRef = useRef(null);
  const isOut = (item.direction || "").toLowerCase() === "money out";
  const categories = isOut ? OUT_CATEGORIES : IN_CATEGORIES;
  const hasVendor = !!(item.vendor && item.vendor.trim());
  const completed = !!answer.category;

  const toggleCategory = (c) =>
    onChange({ category: answer.category === c ? undefined : c });
  const toggleTax = (t) => onChange({ tax: answer.tax === t ? undefined : t });

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const readers = files.map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result || "";
            const base64 = String(result).split(",")[1] || "";
            resolve({
              filename: file.name,
              contentType: file.type || "application/octet-stream",
              base64,
              preview: file.type.startsWith("image/") ? String(result) : null,
            });
          };
          reader.readAsDataURL(file);
        })
    );
    Promise.all(readers).then((newFiles) => {
      onChange({ files: [...(answer.files || []), ...newFiles] });
    });
    e.target.value = "";
  };

  const removeFile = (i) => {
    const next = [...(answer.files || [])];
    next.splice(i, 1);
    onChange({ files: next });
  };

  return (
    <div
      className="bg-white rounded-2xl p-4 gb-transition gb-fade-up"
      style={{
        boxShadow: "0 4px 18px rgba(11, 61, 46, 0.08)",
        border: completed ? "2px solid #16a34a" : "2px solid transparent",
        animationDelay: `${Math.min(index * 60, 400)}ms`,
      }}
      data-testid={`txn-card-${index}`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-3">
        <p className="font-bold text-[15px] text-[#12211b] leading-snug flex-1">
          {item.memo || "Transaction"}
        </p>
        <div className="text-right shrink-0">
          <div
            className="font-bold text-[16px]"
            style={{ color: isOut ? "#b4432f" : "#127a4b" }}
            data-testid={`txn-amount-${index}`}
          >
            {fmtMoney(item.amount)}
          </div>
          <div className="text-[10px] font-semibold tracking-wide text-gray-400 -mt-0.5">
            {isOut ? "PAID" : "RECEIVED"}
          </div>
        </div>
      </div>

      <p className="text-[12px] text-gray-500 mt-1">
        {fmtDate(item.date)} · {item.account || "—"}
      </p>

      {/* Direction pill */}
      <span
        className="inline-block mt-2 text-[11px] font-semibold rounded-full px-2.5 py-1"
        style={
          isOut
            ? { backgroundColor: "#fbe6e0", color: "#b4432f" }
            : { backgroundColor: "#d9f0e3", color: "#127a4b" }
        }
      >
        {isOut ? "Money OUT — you paid" : "Money IN — you received"}
      </span>

      {/* Question + categories */}
      <p className="mt-4 text-[14px] font-semibold text-[#0B3D2E]">
        {isOut ? "What was this payment for?" : "What was this deposit?"}
      </p>
      <div className="flex flex-wrap gap-2 mt-2">
        {categories.map((c) => (
          <Chip
            key={c}
            label={c}
            selected={answer.category === c}
            onClick={() => toggleCategory(c)}
            testid={`txn-${index}-cat-${categories.indexOf(c)}`}
          />
        ))}
      </div>

      {/* Vendor (only if none) */}
      {!hasVendor && (
        <div className="mt-4">
          <label className="text-[13px] font-semibold text-[#0B3D2E]">
            {isOut ? "Who was it paid to?" : "Who was it from?"}{" "}
            <span className="font-normal text-gray-400">(name if you know it)</span>
          </label>
          <input
            type="text"
            value={answer.vendor || ""}
            onChange={(e) => onChange({ vendor: e.target.value })}
            data-testid={`txn-${index}-vendor`}
            className="mt-1.5 w-full h-11 px-3 rounded-xl border border-[#dbe7e0] bg-[#f9fbfa] text-[14px] outline-none focus:border-[#16a34a]"
            placeholder="e.g. Costco"
          />
        </div>
      )}

      {/* Purpose */}
      <div className="mt-4">
        <label className="text-[13px] font-semibold text-[#0B3D2E]">
          Any detail?{" "}
          <span className="font-normal text-gray-400">
            (optional — e.g. "fuel for the Ford", "owner's personal")
          </span>
        </label>
        <textarea
          value={answer.purpose || ""}
          onChange={(e) => onChange({ purpose: e.target.value })}
          data-testid={`txn-${index}-purpose`}
          rows={2}
          className="mt-1.5 w-full px-3 py-2 rounded-xl border border-[#dbe7e0] bg-[#f9fbfa] text-[14px] outline-none focus:border-[#16a34a] resize-none"
          placeholder="Add a note…"
        />
      </div>

      {/* Tax */}
      <div className="mt-4">
        <label className="text-[13px] font-semibold text-[#0B3D2E]">
          Sales tax on it?{" "}
          <span className="font-normal text-gray-400">(optional — leave blank if unsure)</span>
        </label>
        <div className="flex flex-wrap gap-2 mt-2">
          {TAX_OPTIONS.map((t) => (
            <Chip
              key={t}
              label={t}
              selected={answer.tax === t}
              onClick={() => toggleTax(t)}
              testid={`txn-${index}-tax-${TAX_OPTIONS.indexOf(t)}`}
            />
          ))}
        </div>
      </div>

      {/* Attachment */}
      <div className="mt-4">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={handleFiles}
          data-testid={`txn-${index}-file-input`}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          data-testid={`txn-${index}-attach-btn`}
          className="flex items-center gap-2 text-[13px] font-semibold text-[#16784A] border border-dashed border-[#9fc7b2] rounded-xl px-4 py-2.5 gb-transition active:scale-95"
        >
          <Paperclip className="w-4 h-4" /> Add receipt / photo
        </button>
        {(answer.files || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid={`txn-${index}-thumbs`}>
            {(answer.files || []).map((f, i) => (
              <div key={i} className="relative">
                {f.preview ? (
                  <img
                    src={f.preview}
                    alt={f.filename}
                    className="w-16 h-16 object-cover rounded-lg border border-[#dbe7e0]"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg border border-[#dbe7e0] bg-[#f4f8f5] flex items-center justify-center text-[10px] text-gray-500 px-1 text-center">
                    PDF
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  data-testid={`txn-${index}-remove-file-${i}`}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#b4432f] text-white flex items-center justify-center shadow"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
